import { compareDesc } from "date-fns";
import { useMemo } from "react";
import { useEffect } from "react";
import { useFocusAwarePolling } from "hooks/useFocusAwarePolling";
import { setPendingImports } from "store/dataImportSlice";
import type { Job, TJobMetadata } from "store/jobsStore";
import { ProcessingJobStatus, fetchActiveJobs, fetchAnalyticsJobs } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TImportTargets } from "types";
import { ImportTargetsEnum } from "types";

const importTypes = Object.keys(ImportTargetsEnum);

const usefulImportTypes = importTypes.filter(
    (item) => !["compute-analytics", "handle-interforum-files"].includes(item),
);

/**
 * Fetches active jobs (QUEUED/PROCESSING/IDLE) periodically.
 * Mounted at app root — keeps the navigation badge and import status up to date for all users.
 */
export function useJobs(accountType) {
    const dispatch = useAppDispatch();
    const jobsLength = +(sessionStorage.getItem("jobs_length") ?? 0);
    const pendingImports = JSON.parse(sessionStorage.getItem("pending_imports") ?? "[]");

    useEffect(() => {
        if (accountType === "beneficiary" || !accountType) return;
        const importsToListen = pendingImports.filter((item) => usefulImportTypes.includes(item.target));
        dispatch(setPendingImports(importsToListen));

        dispatch(fetchActiveJobs()).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                if (jobsLength === (resp.payload as Job[])?.length) {
                    sessionStorage.removeItem("pending_imports");
                    sessionStorage.removeItem("jobs_length");
                }
            }
        });

        const interval = setInterval(() => {
            if (document.hasFocus() && !document.hidden) {
                dispatch(fetchActiveJobs()).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (jobsLength === (resp.payload as Job[])?.length) {
                            sessionStorage.removeItem("pending_imports");
                            sessionStorage.removeItem("jobs_length");
                        }
                    }
                });
            }
        }, 60000);

        return () => {
            clearInterval(interval);
        };
    }, [dispatch, accountType]);
}

/**
 * Polls active jobs while `enabled` (e.g. while an import is in progress on a dedicated page).
 */
export function useFetchJobsPolling({ enabled, intervalMs }: { enabled: boolean; intervalMs: number }) {
    const dispatch = useAppDispatch();

    useFocusAwarePolling({
        enabled,
        intervalMs,
        onPoll: () => {
            dispatch(fetchActiveJobs());
        },
    });
}

/**
 * Fetches Analytics jobs periodically.
 */
export function useAnalyticsJobs(usesAnalytics: boolean) {
    const dispatch = useAppDispatch();

    useFocusAwarePolling({
        enabled: usesAnalytics,
        intervalMs: 7000,
        onPoll: () => {
            dispatch(fetchAnalyticsJobs());
        },
    });
}

/**
 * Returns the most recent completed job (DONE or FAILED) for a given import type,
 * optionally filtered by metadata fields.
 * Requires fetchLastCompletedJob to have been dispatched for the relevant importType.
 */
export function useLastCompletedJob(importType: TImportTargets, metadataFilter?: Partial<TJobMetadata>): Job | null {
    const jobs = useAppSelector((state) => state.jobs.jobs);
    const filterKey = metadataFilter ? JSON.stringify(metadataFilter) : undefined;

    return useMemo(() => {
        if (!jobs?.length) return null;

        const filter: Partial<TJobMetadata> | undefined = filterKey ? JSON.parse(filterKey) : undefined;

        const matching = jobs.filter((j) => {
            if (j.importType !== importType) return false;
            if (j.status !== ProcessingJobStatus.DONE && j.status !== ProcessingJobStatus.FAILED) return false;
            if (filter) {
                for (const [key, value] of Object.entries(filter)) {
                    if (j.metadata?.[key as keyof TJobMetadata] !== value) return false;
                }
            }
            return true;
        });

        if (!matching?.length) return null;

        return matching.sort((a, b) => compareDesc(new Date(a.modifiedAt ?? 0), new Date(b.modifiedAt ?? 0)))[0];
    }, [jobs, importType, filterKey]);
}
